/**
 * Geometric primitives for pose metrics.
 *
 * Everything here works on the **image-plane projection of `worldLandmarks`**:
 * the x and y components only, in metres, with the origin at the hip centre.
 * The z axis is deliberately dropped. MediaPipe's depth estimate is weakly
 * supervised and far noisier than x/y, and every metric in the MVP is a joint
 * angle whose error would be dominated by that noise. Working in the projection
 * keeps the numbers trustworthy; the declared camera view is what tells us
 * which anatomical plane the projection corresponds to.
 *
 * Verified coordinate conventions (measured, not assumed — see the axis probe
 * in the commit that introduced this file):
 *   - origin is the midpoint of the hips
 *   - up is NEGATIVE y (shoulders sit at y ≈ -0.49 on a standing adult)
 *   - positive x is the athlete's LEFT, which is the right-hand side of the
 *     image when they face the camera
 *   - units are metres
 */
import { POSE_LANDMARKS as L } from "../params";

const RAD_TO_DEG = 180 / Math.PI;

/** World up, in the projection. Assumes the camera is roughly level. */
export const UP = { x: 0, y: -1 };

export function sub(a, b) {
  return { x: a.x - b.x, y: a.y - b.y };
}

export function midpoint(a, b) {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

export function length(v) {
  return Math.hypot(v.x, v.y);
}

export function normalize(v) {
  const len = length(v);
  return len === 0 ? { x: 0, y: 0 } : { x: v.x / len, y: v.y / len };
}

export function dot(a, b) {
  return a.x * b.x + a.y * b.y;
}

/** 2D cross product, i.e. the z component of the 3D cross. Sign gives turn direction. */
export function cross(a, b) {
  return a.x * b.y - a.y * b.x;
}

/**
 * Signed angle from vector `from` to vector `to`, in degrees, range (-180, 180].
 * Positive when `cross(from, to)` is positive.
 *
 * What that means visually depends on `from`, because y points down: reversing
 * the reference vector flips the handedness. Callers that expose a signed angle
 * pin its direction with a test rather than reasoning about it.
 */
export function signedAngle(from, to) {
  return RAD_TO_DEG * Math.atan2(cross(from, to), dot(from, to));
}

/** Unsigned angle at joint `b` formed by `a-b-c`, in degrees, range [0, 180]. */
export function angleAtJoint(a, b, c) {
  const ba = sub(a, b);
  const bc = sub(c, b);
  // atan2 of |cross| against dot stays stable near 0 and 180 degrees, where
  // acos(dot / |a||b|) loses precision badly.
  return RAD_TO_DEG * Math.atan2(Math.abs(cross(ba, bc)), dot(ba, bc));
}

/**
 * The trunk frame: hip centre, shoulder centre, and the unit vector pointing
 * from hips to shoulders. All arm angles are measured relative to this rather
 * than to the world, so that leaning does not masquerade as arm movement.
 */
export function trunkAxis(pose) {
  const hip = midpoint(pose[L.LEFT_HIP], pose[L.RIGHT_HIP]);
  const shoulder = midpoint(pose[L.LEFT_SHOULDER], pose[L.RIGHT_SHOULDER]);
  return { hip, shoulder, up: normalize(sub(shoulder, hip)) };
}

/**
 * Trunk tilt away from world vertical, in degrees. Positive tilts towards +x
 * (the right of the image). Read it as sagittal lean from a side view and as
 * lateral sway from a front view.
 */
export function trunkTilt(pose) {
  return signedAngle(UP, trunkAxis(pose).up);
}

/**
 * Angle of the upper arm away from hanging straight down along the trunk.
 *
 * 0 means the arm lies along the trunk pointing down; magnitude grows as the
 * arm rises, reaching 180 when it is straight overhead. The sign says which
 * way the arm went: positive towards +x, negative towards -x.
 *
 * From a side view this is the swing angle in the sagittal plane. From a front
 * view the same number reads as elevation with lateral flare mixed in, which is
 * why the front view scores symmetry rather than absolute swing.
 */
export function armAngleFromTrunk(pose, side) {
  const { up } = trunkAxis(pose);
  const down = { x: -up.x, y: -up.y };
  const shoulder = pose[side === "left" ? L.LEFT_SHOULDER : L.RIGHT_SHOULDER];
  const elbow = pose[side === "left" ? L.LEFT_ELBOW : L.RIGHT_ELBOW];
  // Negated so that positive means +x for both this and trunkTilt. Measuring
  // from `down` rather than from `UP` reverses the cross-product sign, and
  // without this the two signed angles would disagree about which way is which.
  return -signedAngle(down, sub(elbow, shoulder));
}

/** Elbow angle in degrees; 180 is a straight arm. */
export function elbowAngle(pose, side) {
  const isLeft = side === "left";
  return angleAtJoint(
    pose[isLeft ? L.LEFT_SHOULDER : L.RIGHT_SHOULDER],
    pose[isLeft ? L.LEFT_ELBOW : L.RIGHT_ELBOW],
    pose[isLeft ? L.LEFT_WRIST : L.RIGHT_WRIST]
  );
}

/** Mean visibility of the arm chain, used to pick the near arm in a side view. */
export function armVisibility(pose, side) {
  const isLeft = side === "left";
  const chain = [
    pose[isLeft ? L.LEFT_SHOULDER : L.RIGHT_SHOULDER],
    pose[isLeft ? L.LEFT_ELBOW : L.RIGHT_ELBOW],
    pose[isLeft ? L.LEFT_WRIST : L.RIGHT_WRIST],
  ];
  let total = 0;
  for (const p of chain) {
    total += p.visibility != null ? p.visibility : 1;
  }
  return total / chain.length;
}

/**
 * Elevation of the upper arm away from hanging down, in degrees, 0 to 180.
 *
 * This is the measure to take ranges over, NOT the signed angle. A shoulder is
 * a bounded joint: the arm cannot exceed 180 degrees from the trunk axis and
 * cannot rotate continuously past it. Unwrapping the signed angle — which is
 * what this module used to do — treats the joint as if it could, so a single
 * crossing of the overhead point permanently shifts everything after it by 360
 * and produces readings like -520 degrees for an arm that never left its normal
 * range. Unwrapping is correct for a quantity that genuinely revolves; it is
 * wrong for a joint angle.
 *
 * Taking the magnitude puts the wrap point at straight-overhead, which the arm
 * only touches instantaneously, and going past it simply comes back down the
 * same scale. The direction the arm travelled is still available from the
 * signed angle where it is needed.
 */
export function armElevation(pose, side) {
  return Math.abs(armAngleFromTrunk(pose, side));
}

/** Knee angle in degrees; 180 is a straight leg. */
export function kneeAngle(pose, side) {
  const isLeft = side === "left";
  return angleAtJoint(
    pose[isLeft ? L.LEFT_HIP : L.RIGHT_HIP],
    pose[isLeft ? L.LEFT_KNEE : L.RIGHT_KNEE],
    pose[isLeft ? L.LEFT_ANKLE : L.RIGHT_ANKLE]
  );
}

/**
 * Angle at the hip formed by shoulder-hip-knee, in degrees, range [0, 180].
 *
 * 180 is lying flat or standing straight; it closes as the trunk and the legs
 * fold towards each other. Measured in the projection like everything else, so
 * it is a sagittal angle from a side view and close to meaningless from the
 * front, where the fold happens along the axis that was dropped.
 */
export function hipAngle(pose, side) {
  const isLeft = side === "left";
  return angleAtJoint(
    pose[isLeft ? L.LEFT_SHOULDER : L.RIGHT_SHOULDER],
    pose[isLeft ? L.LEFT_HIP : L.RIGHT_HIP],
    pose[isLeft ? L.LEFT_KNEE : L.RIGHT_KNEE]
  );
}

/**
 * Visibility of the chain a fold at the hip is measured through: shoulder, hip
 * and knee. Neither `swingVisibility` (arm and trunk) nor `legVisibility`
 * (knee down) covers it — a V-sit needs the shoulder and the knee at once, and
 * the ankle it does not need is the landmark most often guessed at on the floor.
 */
export function foldVisibility(pose, side) {
  const isLeft = side === "left";
  return Math.min(
    visibilityOf(pose[isLeft ? L.LEFT_SHOULDER : L.RIGHT_SHOULDER]),
    visibilityOf(pose[isLeft ? L.LEFT_HIP : L.RIGHT_HIP]),
    visibilityOf(pose[isLeft ? L.LEFT_KNEE : L.RIGHT_KNEE])
  );
}

/**
 * How far the heel sits above the toe, in metres. Positive is raised.
 *
 * Not zero on a flat foot: the heel landmark is anatomically above the toes, by
 * roughly a third of the foot's length. Divide by `footLength()` and compare
 * against a threshold well clear of that, rather than against zero.
 */
export function heelLift(pose, side) {
  const isLeft = side === "left";
  const heel = pose[isLeft ? L.LEFT_HEEL : L.RIGHT_HEEL];
  const toe = pose[isLeft ? L.LEFT_FOOT_INDEX : L.RIGHT_FOOT_INDEX];
  // Up is negative y, so the toe being lower means a larger y.
  return toe.y - heel.y;
}

/** Heel-to-toe distance in metres, the scale tiptoe height is expressed in. */
export function footLength(pose, side) {
  const isLeft = side === "left";
  return length(
    sub(
      pose[isLeft ? L.LEFT_FOOT_INDEX : L.RIGHT_FOOT_INDEX],
      pose[isLeft ? L.LEFT_HEEL : L.RIGHT_HEEL]
    )
  );
}

/**
 * How far the head is pitched from square to the trunk, in degrees. 0 would be
 * the ear-to-nose line exactly perpendicular to the spine.
 *
 * Measured against the trunk, not against gravity, for the same reason the arm
 * angles are: a neutral neck means the head sits in line with the torso, and
 * measuring against the world charges the head for whatever the trunk is doing.
 * On real footage the trunk tilted across a 48-degree range and the two
 * references disagreed by about 50 degrees, so the distinction is not academic.
 *
 * The zero is a property of the athlete's anatomy, not of good form — the
 * ear-to-nose line is not exactly perpendicular to the spine on anyone. Compare
 * against `STATE.posture.neckOffset`, which is a setting for that reason.
 */
export function headPitchFromTrunk(pose, side) {
  const { up } = trunkAxis(pose);
  const ear = pose[side === "left" ? L.LEFT_EAR : L.RIGHT_EAR];
  const head = sub(pose[L.NOSE], ear);
  // The magnitude first: the signed angle's own sign flips with which way the
  // athlete faces, which is not what is being asked.
  return Math.abs(signedAngle(up, head)) - 90;
}

/**
 * Which way along x the athlete faces: +1 when their nose leads towards +x.
 *
 * Needed by anything comparing horizontal positions, such as whether the head
 * is ahead of the knee. The nose-to-ear offset is the most reliable available
 * reference: on real footage it disagreed with its own median on 57 frames of
 * 666, against 222 for heel-to-toe, because the ear holds near-perfect
 * visibility while the foot does not.
 *
 * Still worth taking over a span rather than per frame — see `FacingTracker`.
 */
export function facingOffset(pose, side) {
  return pose[L.NOSE].x - pose[side === "left" ? L.LEFT_EAR : L.RIGHT_EAR].x;
}

/**
 * Horizontal distance from the knee to `point`, in metres, along the direction
 * the athlete faces. Positive is ahead of the knee.
 */
export function aheadOfKnee(point, pose, side, facing) {
  return (
    facing * (point.x - pose[side === "left" ? L.LEFT_KNEE : L.RIGHT_KNEE].x)
  );
}

/** The shoulder midpoint, which is what "chest" means in the posture criteria. */
export function chest(pose) {
  return midpoint(pose[L.LEFT_SHOULDER], pose[L.RIGHT_SHOULDER]);
}

/**
 * Lowest visibility of the landmarks the rep count itself depends on: the arm
 * being followed and the trunk it is measured against.
 *
 * Deliberately not the legs. Counting is a question about the arm, so a take
 * framed from the knees up should still count — it just cannot be scored for
 * tiptoes or knee flexion. The legs are also the less reliable half of the pose:
 * on the reference recordings more frames fall below the visibility threshold on
 * the legs than on the arm and trunk, so a leg gate would break accumulation on
 * frames where the arm itself is perfectly clear.
 */
export function swingVisibility(pose, side) {
  const isLeft = side === "left";
  return Math.min(
    armVisibility(pose, side),
    visibilityOf(pose[isLeft ? L.LEFT_HIP : L.RIGHT_HIP]),
    visibilityOf(pose[isLeft ? L.LEFT_SHOULDER : L.RIGHT_SHOULDER])
  );
}

function visibilityOf(point) {
  return point.visibility != null ? point.visibility : 1;
}

/** Mean visibility of the leg chain. Used to refuse frames the model is guessing. */
export function legVisibility(pose, side) {
  const isLeft = side === "left";
  const chain = [
    pose[isLeft ? L.LEFT_KNEE : L.RIGHT_KNEE],
    pose[isLeft ? L.LEFT_ANKLE : L.RIGHT_ANKLE],
    pose[isLeft ? L.LEFT_HEEL : L.RIGHT_HEEL],
  ];
  let lowest = 1;
  for (const p of chain) {
    lowest = Math.min(lowest, visibilityOf(p));
  }
  // The lowest, not the mean: one guessed landmark is enough to invalidate a
  // knee angle or a heel height, and a mean lets two good ones hide it.
  return lowest;
}

/**
 * Angle at the ankle formed by knee-ankle-toe, in degrees.
 *
 * 180 is the foot continuing the line of the shin — pointed toes. It closes as
 * the foot is pulled towards the shin.
 *
 * Not `heelLift`, which the squat jump uses: that one is the heel's height above
 * the toe in WORLD up, so it only means "the heel came off the floor" for an
 * athlete the right way up. Upside down there is no floor under the foot and no
 * fixed direction for it to be lifted in, so what is left is the joint angle,
 * which is the same measurement whichever way round the body is.
 */
export function ankleAngle(pose, side) {
  const isLeft = side === "left";
  return angleAtJoint(
    pose[isLeft ? L.LEFT_KNEE : L.RIGHT_KNEE],
    pose[isLeft ? L.LEFT_ANKLE : L.RIGHT_ANKLE],
    pose[isLeft ? L.LEFT_FOOT_INDEX : L.RIGHT_FOOT_INDEX]
  );
}

/**
 * Angle at the shoulder formed by wrist-shoulder-hip, in degrees.
 *
 * 180 is the arm in line with the trunk, which upside down is the shoulder
 * stacked over the hand and the thing a handstand asks for. It closes as the
 * shoulder shuts, which is what a beginner's handstand does under load.
 *
 * Not `armAngleFromTrunk`: that measures from the trunk's own axis and is signed
 * for a swing. Here the quantity wanted is simply how straight the hand, the
 * shoulder and the hip are, which is a joint angle like the knee's.
 */
export function shoulderAngle(pose, side) {
  const isLeft = side === "left";
  return angleAtJoint(
    pose[isLeft ? L.LEFT_WRIST : L.RIGHT_WRIST],
    pose[isLeft ? L.LEFT_SHOULDER : L.RIGHT_SHOULDER],
    pose[isLeft ? L.LEFT_HIP : L.RIGHT_HIP]
  );
}

/**
 * Visibility of the whole chain a handstand is read through: wrist, elbow,
 * shoulder, hip, knee, ankle.
 *
 * The longest gate in the app, and deliberately so. Every other drill can
 * afford to ignore one end of the body — the swing ignores the legs, the V-sit
 * ignores the ankles — because what it measures lives at the other. A line from
 * the hand to the foot is the measurement here, so a guess anywhere along it is
 * a guess about the whole thing.
 */
export function handstandVisibility(pose, side) {
  const isLeft = side === "left";
  const chain = [
    pose[isLeft ? L.LEFT_WRIST : L.RIGHT_WRIST],
    pose[isLeft ? L.LEFT_ELBOW : L.RIGHT_ELBOW],
    pose[isLeft ? L.LEFT_SHOULDER : L.RIGHT_SHOULDER],
    pose[isLeft ? L.LEFT_HIP : L.RIGHT_HIP],
    pose[isLeft ? L.LEFT_KNEE : L.RIGHT_KNEE],
    pose[isLeft ? L.LEFT_ANKLE : L.RIGHT_ANKLE],
  ];
  let lowest = 1;
  for (const p of chain) {
    lowest = Math.min(lowest, visibilityOf(p));
  }
  return lowest;
}

/**
 * Which way the body leans off vertical between the hands and the feet, in
 * metres along x. Positive means the feet are towards +x of the hands.
 *
 * This is how the wall is found without seeing it: a wall handstand rests
 * against something, so the body is not plumb — it leans the way the wall is.
 * Compared against which way the athlete faces (`facingOffset`), it says chest
 * to the wall or back to it. It is small, a few centimetres, so it is reported
 * and never judged.
 */
export function wallLean(pose, side) {
  const isLeft = side === "left";
  const wrist = pose[isLeft ? L.LEFT_WRIST : L.RIGHT_WRIST];
  const ankle = pose[isLeft ? L.LEFT_ANKLE : L.RIGHT_ANKLE];
  return ankle.x - wrist.x;
}

/**
 * How far the ear sits off the line of the arm, as an angle at the shoulder, in
 * degrees. Signed by which side of the arm the ear falls on.
 *
 * This is the handstand's head criterion: the athlete asked for the ears between
 * the arms, and that is a question about which side of the shoulder-to-wrist
 * line the head is on rather than about how far it is from it. The magnitude
 * alone does not separate anything — on the reference take a good hold reads 8
 * degrees off the line and 28 degrees off it at different moments, and a
 * deliberately broken one reads 7 — but the SIGN flips exactly where the posture
 * breaks.
 *
 * The sign here is the raw side of the arm, which depends on whether the athlete
 * faces +x or -x, so it means nothing on its own. `facingOffset` turns it into
 * "behind the arms or tucked in front of them", and that is deliberately left to
 * the caller: which way the athlete faces is a property of the take rather than
 * of one frame, which is the same reason `aheadOfKnee` takes a facing argument.
 *
 * An angle rather than the centimetres it is derived from, because a distance
 * would scale with the athlete and with how far he stands from the camera.
 */
export function earOffArmLine(pose, side) {
  const isLeft = side === "left";
  const shoulder = pose[isLeft ? L.LEFT_SHOULDER : L.RIGHT_SHOULDER];
  const arm = sub(pose[isLeft ? L.LEFT_WRIST : L.RIGHT_WRIST], shoulder);
  const head = sub(pose[isLeft ? L.LEFT_EAR : L.RIGHT_EAR], shoulder);
  if (length(arm) < 1e-6 || length(head) < 1e-6) {
    return null;
  }
  return signedAngle(arm, head);
}
